/**
 * Single source of truth for all copy/imagery on the Ultrahuman × Les Mills page.
 * Replace placeholder strings/paths with Figma-sourced values as they land.
 */

import { getCompressedAssetUrl } from 'lib/config/assetUrls';

/** Minimal next-intl translate signature this content builder consumes. */
type Translate = (
  key: string,
  values?: Record<string, string | number>,
) => string;

const HERO_CTA_HREF = {
  pathname: '/ring/buy',
  query: {
    flow: 'les-mills',
    affiliateCode: 'lesmills',
    invite: 'lesmills15',
  },
};

const WHAT_YOU_GET_VIDEO = {
  src: getCompressedAssetUrl('/web_v2/uh-x-les-mills/what-you-get/v2/00-overview.mp4') as
    | string
    | undefined,
  poster: getCompressedAssetUrl('/web_v2/uh-x-les-mills/what-you-get/poster.png') as
    | string
    | undefined,
  // Delay (ms) after the section enters the viewport before video playback starts.
  playDelayMs: 2500,
};

const PROGRAMS_CAROUSEL_ITEMS = [
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/bodypump.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/bodypump.svg',
    label: 'BODYPUMP',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/bodycombat.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/bodycombat.svg',
    label: 'BODYCOMBAT',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/bodyattack.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/bodyattack.svg',
    label: 'BODYATTACK',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/grit.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/grit.svg',
    label: 'LES MILLS GRIT',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/yoga.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/yoga.svg',
    label: 'LES MILLS YOGA',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/pilates.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/pilates.svg',
    label: 'LES MILLS PILATES',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/shapes.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/shapes.svg',
    label: 'LES MILLS SHAPES',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/core.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/core.svg',
    label: 'LES MILLS CORE',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/rpm.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/rpm.svg',
    label: 'RPM',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/sprint.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/sprint.svg',
    label: 'LES MILLS SPRINT',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/the-trip.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/the-trip.svg',
    label: 'THE TRIP',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/bodybalance.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/bodybalance.svg',
    label: 'BODYBALANCE',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/dance.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/dance.svg',
    label: 'LES MILLS DANCE',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/functional-strength.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/functional-strength.svg',
    label: 'FUNCTIONAL STRENGTH',
  },
  {
    image: '/web_v2/uh-x-les-mills/integration/carousel/thrive.webp',
    logo: '/web_v2/uh-x-les-mills/integration/carousel/logos/thrive.svg',
    label: 'LES MILLS THRIVE',
  },
];

export const getLesMillsContent = (t: Translate) => ({
  hero: {
    title: t('hero.title'),
    ctaHref: HERO_CTA_HREF,
  },
  whatYouGetItems: [
    {
      title: t('whatYouGet.items.0.title'),
      caption: t('whatYouGet.items.0.caption'),
      iconLabel: t('whatYouGet.items.0.iconLabel'),
      icon: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/what-you-get/icons/icon1.png',
      ),
    },
    {
      title: t('whatYouGet.items.1.title'),
      caption: t('whatYouGet.items.1.caption'),
      iconLabel: t('whatYouGet.items.1.iconLabel'),
      icon: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/what-you-get/icons/icon2.png',
      ),
    },
    {
      title: t('whatYouGet.items.2.title'),
      caption: t('whatYouGet.items.2.caption'),
      iconLabel: t('whatYouGet.items.2.iconLabel'),
      icon: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/what-you-get/icons/icon3.png',
      ),
    },
  ],
  whatYouGetVideo: WHAT_YOU_GET_VIDEO,
  manifesto: {
    title: t('manifesto.title'),
    subtitle: t('manifesto.subtitle'),
    imageLabel: t('manifesto.imageLabel'),
    image: getCompressedAssetUrl('/web_v2/uh-x-les-mills/manifesto/hero.png') as
      | string
      | undefined,
  },
  getStartedSteps: [
    {
      title: t('getStarted.steps.0.title'),
      subtitle: t('getStarted.steps.0.subtitle'),
      imageLabel: t('getStarted.steps.0.imageLabel'),
      image: {
        desktop: '/web_v2/uh-x-les-mills/get-started/step-1-desktop.png',
        mobile: '/web_v2/uh-x-les-mills/get-started/step-1-mobile.png',
      },
      badgeColor: '#FFFFFF',
    },
    {
      title: t('getStarted.steps.1.title'),
      subtitle: t('getStarted.steps.1.subtitle'),
      imageLabel: t('getStarted.steps.1.imageLabel'),
      image: {
        desktop: '/web_v2/uh-x-les-mills/get-started/step-2-desktop.png',
        mobile: '/web_v2/uh-x-les-mills/get-started/step-2-mobile.png',
      },
      badgeColor: '#F6F6F6',
    },
    {
      title: t('getStarted.steps.2.title'),
      subtitle: t('getStarted.steps.2.subtitle'),
      imageLabel: t('getStarted.steps.2.imageLabel'),
      image: {
        desktop: '/web_v2/uh-x-les-mills/get-started/step-3-desktop.png',
        mobile: '/web_v2/uh-x-les-mills/get-started/step-3-mobile.png',
      },
      badgeColor: '#FFFFFF',
    },
    {
      title: t('getStarted.steps.3.title'),
      subtitle: t('getStarted.steps.3.subtitle'),
      imageLabel: t('getStarted.steps.3.imageLabel'),
      image: {
        desktop: '/web_v2/uh-x-les-mills/get-started/step-4-desktop.png',
        mobile: '/web_v2/uh-x-les-mills/get-started/step-4-mobile.png',
      },
      badgeColor: '#FFFFFF',
    },
  ].map((step) => ({
    ...step,
    image: {
      desktop: getCompressedAssetUrl(step.image.desktop),
      mobile: getCompressedAssetUrl(step.image.mobile),
    },
  })),
  integrationItems: [
    {
      title: t('integration.items.4.title'),
      description: t('integration.items.4.description'),
      imageLabel: t('integration.items.4.imageLabel'),
      image: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/integration/v2/fitness-goals.png',
      ),
      layout: 'column' as const,
    },
    {
      title: t('integration.items.0.title'),
      description: t('integration.items.0.description'),
      imageLabel: t('integration.items.0.imageLabel'),
      image: getCompressedAssetUrl('/web_v2/uh-x-les-mills/integration/readiness.png'),
      layout: 'column' as const,
    },
    {
      title: t('integration.items.5.title'),
      description: t('integration.items.5.description'),
      imageLabel: t('integration.items.5.imageLabel'),
      image: getCompressedAssetUrl('/web_v2/uh-x-les-mills/integration/v2/00-auto-tracking.png'),
      imageMobile: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/integration/v2/00-auto-tracking-mobile.png',
      ),
      layout: 'row' as const,
      colSpanLg: 2,
    },
    {
      title: t('integration.items.3.title'),
      description: t('integration.items.3.description'),
      imageLabel: t('integration.items.3.imageLabel'),
      image: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/integration/v2/cycle-adjusted.png',
      ),
      layout: 'column' as const,
    },
    {
      title: t('integration.items.1.title'),
      description: t('integration.items.1.description'),
      imageLabel: t('integration.items.1.imageLabel'),
      image: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/integration/v2/biomarker-matching.png',
      ),
      imageMobile: getCompressedAssetUrl(
        '/web_v2/uh-x-les-mills/integration/v2/biomarker-matching-mobile.png',
      ),
      layout: 'column' as const,
    },
    {
      title: t('integration.items.2.title'),
      description: t('integration.items.2.description'),
      imageLabel: t('integration.items.2.imageLabel'),
      carousel: PROGRAMS_CAROUSEL_ITEMS.map((item) => ({
        ...item,
        image: getCompressedAssetUrl(item.image),
        logo: getCompressedAssetUrl(item.logo),
      })),
      layout: 'column' as const,
      colSpanLg: 2,
    },
  ],
  upcomingItems: [
    {
      title: t('upcoming.items.0.title'),
      description: t('upcoming.items.0.description'),
      imageLabel: t('upcoming.items.0.imageLabel'),
      image: getCompressedAssetUrl('/web_v2/uh-x-les-mills/upcoming/v2/00-12-week-plan.png'),
    },
    {
      title: t('upcoming.items.2.title'),
      description: t('upcoming.items.2.description'),
      imageLabel: t('upcoming.items.2.imageLabel'),
      image: getCompressedAssetUrl('/web_v2/uh-x-les-mills/upcoming/v2/00-monthly-content.png'),
    },
  ],
  optimize: {
    title: t('optimize.title'),
    banner: getCompressedAssetUrl('/web_v2/uh-x-les-mills/optimize/banner.png'),
    disclaimer: t('optimize.disclaimer'),
  },
  faqItems: [
    {
      question: t('faq.items.0.question'),
      answer: t('faq.items.0.answer'),
    },
    {
      question: t('faq.items.1.question'),
      answer: t('faq.items.1.answer'),
    },
    {
      question: t('faq.items.2.question'),
      answer: t('faq.items.2.answer'),
    },
    {
      question: t('faq.items.3.question'),
      answer: t('faq.items.3.answer'),
    },
    {
      question: t('faq.items.4.question'),
      answer: t('faq.items.4.answer'),
    },
  ],
});
